import React from 'react';
import { motion } from 'framer-motion';

const MoodOrb = ({ moodRating, emoji, size = 'md' }) => {
    // Generate gradient colors based on mood rating
    const getGradientColors = (rating) => {
        if (rating <= 2) {
            return ['#3B4B6C', '#1A2540', '#4A5568']; // Deep blue/gray - struggling
        } else if (rating <= 4) {
            return ['#6B5B7C', '#4A3A5C', '#8B7B9C']; // Muted purple - low
        } else if (rating <= 6) {
            return ['#4A7C6B', '#2A5C4B', '#6A9C8B']; // Forest green - neutral
        } else if (rating <= 8) {
            return ['#7C9B4A', '#5C7B2A', '#9CBB6A']; // Warm green - good
        } else {
            return ['#D4A574', '#C49464', '#E4B584']; // Warm golden - excellent
        }
    };

    const colors = getGradientColors(moodRating);
    const animationDuration = 4 - (moodRating / 10) * 2; // Faster animation for higher moods

    const sizeClasses = {
        sm: 'w-20 h-20',
        md: 'w-32 h-32',
        lg: 'w-48 h-48'
    };

    return (
        <motion.div
            className={`relative ${sizeClasses[size]} mood-orb`}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
        >
            {/* Outer glow */}
            <div
                className="absolute inset-0 rounded-full blur-xl opacity-40"
                style={{
                    background: `radial-gradient(circle, ${colors[0]} 0%, transparent 70%)`
                }}
            />
            
            {/* Main orb */}
            <motion.div
                className="absolute inset-2 rounded-full shadow-lg"
                style={{
                    background: `radial-gradient(circle at 30% 30%, ${colors[2]} 0%, ${colors[0]} 50%, ${colors[1]} 100%)`
                }}
                animate={{
                    boxShadow: [
                        `0 0 20px ${colors[0]}40, 0 0 40px ${colors[0]}20`,
                        `0 0 30px ${colors[0]}60, 0 0 60px ${colors[0]}30`,
                        `0 0 20px ${colors[0]}40, 0 0 40px ${colors[0]}20`
                    ]
                }}
                transition={{
                    duration: animationDuration,
                    repeat: Infinity,
                    ease: 'easeInOut'
                }}
            />

            {/* Inner highlight */}
            <div
                className="absolute top-4 left-4 w-1/3 h-1/3 rounded-full opacity-30"
                style={{
                    background: `radial-gradient(circle, white 0%, transparent 70%)`
                }}
            />

            {/* Emoji overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
                <motion.span
                    className={`${size === 'lg' ? 'text-5xl' : size === 'md' ? 'text-3xl' : 'text-2xl'}`}
                    animate={{
                        scale: [1, 1.1, 1]
                    }}
                    transition={{
                        duration: animationDuration,
                        repeat: Infinity,
                        ease: 'easeInOut'
                    }}
                >
                    {emoji}
                </motion.span>
            </div>
        </motion.div>
    );
};

export default MoodOrb;
